import { useState, useEffect } from 'react';
import {
  onAuthStateChanged,
  signInWithPopup,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  signOut,
  type User,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { isTestMode, mockUser, buildMockUser, mockSignIn } from '../testMode';

const base = import.meta.env.BASE_URL.replace(/\/$/, '');

const EMAIL_LINK_SETTINGS = {
  url: window.location.origin + base + '/app',
  handleCodeInApp: true,
};

export function useAuth() {
  const [user, setUser] = useState<User | null>(isTestMode ? mockUser : null);
  const [loading, setLoading] = useState(!isTestMode);

  useEffect(() => {
    if (isTestMode) return;

    if (isSignInWithEmailLink(auth, window.location.href)) {
      const email = localStorage.getItem('gnosis-email-signin');
      if (email) {
        signInWithEmailLink(auth, email, window.location.href)
          .then(() => localStorage.removeItem('gnosis-email-signin'))
          .catch(() => {});
      }
    }

    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
    });
  }, []);

  const signInWithGoogle = async () => {
    if (isTestMode) {
      mockSignIn();
      setUser(mockUser ?? buildMockUser(false));
      setLoading(false);
      return;
    }
    try {
      return await signInWithPopup(auth, googleProvider);
    } catch (err) {
      const e = err as { code?: string; message?: string };
      console.error('[useAuth] signInWithGoogle error:', e.code, e.message);
      throw err;
    }
  };

  const sendEmailLink = async (email: string) => {
    localStorage.setItem('gnosis-email-signin', email);
    await sendSignInLinkToEmail(auth, email, EMAIL_LINK_SETTINGS);
  };

  const logOut = () => signOut(auth);

  return { user, loading, signInWithGoogle, sendEmailLink, logOut };
}
